import { z } from 'zod';
import type { ContentWidget } from './ContentWidget';
import {
    TimelineSlot,
    WidgetType,
    ZodBaseWidgetConfiguration,
    ZodGenericWidgetConfigurationDetails,
} from '../types';
import type { ReachApiResponse } from '../../ReachApi/ReachAPI';
import type { ApiSuccessResponse } from '../../ReachApi/types';

export type ContentTimelineSlotParams = {
    thisloopcount: number;
    story: ContentStory; //TODO: See StoryObject typedef
};

export type ContentTimelineSlot = TimelineSlot<ContentTimelineSlotParams, ContentWidget>;

export const ZodContentWidgetConfigurationDetails = ZodGenericWidgetConfigurationDetails.extend({
    content_type: z.string(),
    landscape: z.string(),
    feed_url: z.string(),
    postnum: z.string(),
    displaynum: z.string(),
    data_list: z.string(),
    parsing_sugar: z.string(),
    url_fetcher_id: z.string(),
    template: z.string(), //Overrides base, required
});

export type ContentWidgetConfigurationDetails = z.infer<
    typeof ZodContentWidgetConfigurationDetails
>;

export const ZodContentWidgetConfiguration = ZodBaseWidgetConfiguration.extend({
    type: z.literal(WidgetType.CONTENT),
    config: ZodContentWidgetConfigurationDetails,
});

export type ContentWidgetConfiguration = z.infer<typeof ZodContentWidgetConfiguration>;

export enum ContentLayoutType {
    PORTRAIT = 'portrait',
    LANDSCAPE = 'landscape',
}

export type ContentStory = {
    id: string; //RawContentStory's id_hash
    date: string;
    timestamp: number | null;
    formattedDate: string;
    title: string | null;
    message: string | null;
    image: string | null; //URL?
    long_message: string | null;
    template: number | string;
    eventDate: string;
    eventTime: string;
    eventEndTime: string;
    eventRoomName: string;
};

export type RawContentStory = {
    author?: string | null;
    date: string; //Rss is string and for some reason data list is a moment
    date_as_timestamp: number | null; //Rss is number, data list is null
    description: string;
    id_hash: string;
    long_description: string;
    media: Array<{ url: string }>;
    publishedAt?: string;
    title: string;
    url?: string;
    urlToImage?: string;
    event_date?: string; //Seemingly only for rss feed data
    event_time?: string; //Seemingly only for rss feed data
    event_end_time?: string; //Seemingly only for rss feed data
    event_room_name?: string; //Seemingly only for rss feed data
};

export type RawDataListStory = {
    rowid: string;
    description: string;
    title: string;
    long_description: string;
};

export interface RawDataListImageStory extends RawDataListStory {
    image: string;
}

export type ContentStoryTemplateData = {
    //Used by both Handlebars and Vue templates
    stories: (ContentStory & {
        fontResize: boolean;
        feedType: string;
    })[];

    //Used by Handlebars templates exclusively
    wid: string;

    //Used by Vue templates exclusively
    widget: {
        id: number;
    };

    metadata: {
        landscape: boolean;
        fontResize: boolean;
        fontProperties: {
            minSize: number;
            maxSize: number;
            ellipsis: boolean;
        };
    };

    //Used for CSS only
    Widget: {
        Id: number;
    };
};

export interface ReachAPIGetFeedResponse extends ReachApiResponse {
    feed: {
        sources: unknown;
        items: RawContentStory[];
    };
}

export type DataListCell = {
    id: string;
    row_id: string;
    row_ordinal: string;
    value: string; // (value known to be used by custom scripts)
    is_raw_json: 'true' | 'false';
    column_ordinal: string;
};

export interface DataGetCellResponse extends ApiSuccessResponse {
    actualSize: number; // (value known to be used by custom scripts)
    rows: DataListCell[][];
}

export type DataListColumn = {
    id: string;
    is_identity: boolean;
    data_list_id: string;
    name: string; // (value known to be used by custom scripts)
    type: string; // Some known values: 'TEXT' | 'IMAGE'
    width: string;
    ordinal: string;
    ordinal_as_alpha: string;
};

export interface DataGetListResponse extends ApiSuccessResponse {
    list: {
        id: string;
        data_list_name: string;
        data_list_type: string;
        columns: DataListColumn[];
    };
}
